import { type Components, type Theme, alpha } from "@mui/material/styles";
/**
 * Componentes de Material-UI con soporte para modo oscuro
 * Usa colores del tema en lugar de valores hardcodeados
 */
export function createComponents(theme: Theme): Components<Theme> {
  const isDark = theme.palette.mode === "dark";
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "0.75rem",
          padding: "0.5rem 1rem",
          fontWeight: 500,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: theme.shadows[2],
          },
        },
        outlined: {
          borderWidth: "1px",
          "&:hover": {
            borderWidth: "1px",
          },
        },
        sizeLarge: {
          padding: "0.75rem 1.5rem",
          fontSize: "1rem",
        },
        sizeSmall: {
          padding: "0.375rem 0.75rem",
          fontSize: "0.875rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "1rem",
          boxShadow: theme.shadows[3],
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "1rem",
          backgroundImage: "none",
          backgroundColor: theme.palette.background.paper,
        },
        elevation1: {
          boxShadow: theme.shadows[1],
        },
        elevation2: {
          boxShadow: theme.shadows[2],
        },
        elevation3: {
          boxShadow: theme.shadows[3],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "0.5rem",
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "0.75rem",
            backgroundColor: theme.palette.background.paper,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "0.75rem",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: "48px",
          padding: "12px 16px",
          borderRadius: "0.5rem",
          "&.Mui-selected": {
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "48px",
        },
        indicator: {
          display: "none",
        },
      },
    },
    // ⚠️ IMPORTANTE: Estilos de MuiDataGrid comentados porque interfieren con DataGridPro
    // DataGridPro usa su propio sistema de estilos más complejo y los overrides causan problemas
    // Si necesitas estilos personalizados, usa sx prop directamente en el componente
    /* 
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          borderRadius: "1rem",
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
        },
        cell: {
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: "12px 16px",
          color: theme.palette.text.primary,
        },
        columnHeaders: {
          backgroundColor: isDark
            ? alpha(theme.palette.background.default, 0.5)
            : theme.palette.grey[50],
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        row: {
          "&:hover": {
            backgroundColor: isDark
              ? alpha(theme.palette.action.hover, 0.8)
              : "rgba(241, 245, 249, 0.5)",
            cursor: "pointer",
          },
        },
      },
    },
    */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "0.75rem",
        },
        standardSuccess: {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.dark,
        },
        standardError: {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.dark,
        },
        standardWarning: {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.dark,
        },
        standardInfo: {
          backgroundColor: theme.palette.info.light,
          color: theme.palette.info.dark,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "1rem",
          boxShadow: theme.shadows[5],
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "0.75rem",
          boxShadow: theme.shadows[3],
          marginTop: "4px",
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
        },
        list: {
          backgroundColor: theme.palette.background.paper,
          padding: "4px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: "0.5rem",
          margin: "2px 4px",
          padding: "8px 12px",
          backgroundColor: theme.palette.background.paper,
          "&:hover": {
            backgroundColor: isDark
              ? alpha(theme.palette.action.hover, 0.8)
              : "#f1f5f9", // slate-100
          },
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark
            ? theme.palette.grey[700]
            : theme.palette.grey[800],
          fontSize: "0.875rem",
          padding: "8px 12px",
          borderRadius: "0.5rem",
        },
        arrow: {
          color: isDark ? theme.palette.grey[700] : theme.palette.grey[800],
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          padding: "4px",
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          "&:hover": {
            backgroundColor: isDark
              ? alpha(theme.palette.action.hover, 0.8)
              : "#f1f5f9", // slate-100
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: "0.5rem",
          margin: "2px 4px",
          "&:hover": {
            backgroundColor: isDark
              ? alpha(theme.palette.action.hover, 0.8)
              : "#f1f5f9", // slate-100
          },
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
        },
        listbox: {
          backgroundColor: theme.palette.background.paper,
          padding: "4px",
        },
        option: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: "0.5rem",
          margin: "2px 4px",
          "&:hover": {
            backgroundColor: isDark
              ? `${alpha(theme.palette.action.hover, 0.8)} !important`
              : "#f1f5f9 !important", // slate-100
          },
          "&.Mui-selected": {
            backgroundColor: `${alpha(
              theme.palette.primary.main,
              0.12
            )} !important`,
            "&:hover": {
              backgroundColor: `${alpha(
                theme.palette.primary.main,
                0.16
              )} !important`,
            },
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },
  };
}
